import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { UserData, getCurrentUser, isAuthenticated, logoutUser, clearAuthData, getAuthToken } from '../api/auth';
import { syncUserData } from '../api/sync';
import { IS_TELEGRAM, getInitData, tgReady, tgExpand } from '../platform';
import { telegramLoginIfLinked } from '../api/auth';

interface UserContextType {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  isLoggedIn: boolean;
  logout: () => void;
  syncUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Sync interval: 5 minutes (like mobile app)
const SYNC_INTERVAL = 5 * 60 * 1000;

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Sync user data from server
  const syncUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      return;
    }

    try {
      console.log('🔄 Syncing user data...');
      const response = await syncUserData(token);
      
      if (response.user) {
        // Merge with existing user data to preserve any local-only fields
        setUser((prevUser) => {
          const updatedUser = {
            ...prevUser,
            ...response.user,
          } as UserData;
          
          // Update localStorage with the merged data
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          return updatedUser;
        });
        
        console.log('✅ User data synced successfully');
      }
    } catch (error) {
      console.error('Failed to sync user data:', error);
      // Don't throw - just log the error and continue
    }
  }, []); // Empty dependency array - this function doesn't depend on any props or state

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      // Inside Telegram: re-authenticate from initData on every cold start.
      if (IS_TELEGRAM) {
        tgReady();
        tgExpand();
        const initData = getInitData();
        if (initData) {
          try {
            const result = await telegramLoginIfLinked(initData);
            if (result && !cancelled) {
              setUser(result.user);
              setIsLoggedIn(true);
              return;
            }
            // Not linked (result === null): fall through and stay logged out.
          } catch (error) {
            console.error('Telegram login-if-linked failed, falling back:', error);
          }
        }
      }

      // Standard web path (and Telegram fallback): restore any saved session.
      try {
        if (isAuthenticated()) {
          const currentUser = getCurrentUser();
          if (currentUser && !cancelled) {
            setUser(currentUser);
            setIsLoggedIn(true);
          } else if (!currentUser) {
            clearAuthData();
          }
        }
      } catch (error) {
        console.error('Error checking authentication status:', error);
        clearAuthData();
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Set up periodic sync when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      // Initial sync after login
      syncUser();

      // Set up periodic sync
      syncIntervalRef.current = setInterval(() => {
        syncUser();
      }, SYNC_INTERVAL);

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
      };
    }
  }, [isLoggedIn, syncUser]); // Only depend on isLoggedIn and syncUser (which never changes now)

  const handleSetUser = (userData: UserData | null) => {
    setUser(userData);
    setIsLoggedIn(!!userData);
  };

  const logout = () => {
    try {
      // Clear sync interval
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      
      logoutUser();
      setUser(null);
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error during logout:', error);
      // Force clear state even if there's an error
      setUser(null);
      setIsLoggedIn(false);
    }
  };

  return (
    <UserContext.Provider value={{ user, setUser: handleSetUser, isLoggedIn, logout, syncUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
